import { supabase } from '@/integrations/supabase/client';

export interface AgentProfile {
  id: string;
  user_id: string;
  onboarding_completed: boolean;
  deals_closed_last_year?: number;
  average_deal_size?: number;
  years_in_real_estate?: number;
  career_stage?: 'new_agent' | 'growing' | 'established' | 'team_leader' | 'top_producer';
  has_team?: boolean;
  team_size?: number;
  primary_market?: string;
  budget_range?: 'low' | 'mid' | 'high';
  pain_points?: string[];
  current_tools?: string[];
  goals?: any[];
  communication_style?: string;
  created_at: string;
  updated_at: string;
}

export interface EnrichedProduct {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  image?: string;
  categories: any;
  price?: number;
  msrp?: number;
  price_duration?: string;
  rating?: number;
  service_type?: string;
  best_for?: any;
  at_a_glance?: any;
  relevance_score: number;
  capabilities_summary: string;
  integration_tags: string[];
  starting_price: number;
}

// Get or create agent profile for current user
export async function getOrCreateAgentProfile(userId: string): Promise<AgentProfile | null> {
  const { data: existing, error: fetchError } = await supabase
    .from('agent_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (existing) return existing as AgentProfile;

  // Create new profile
  const { data: newProfile, error: createError } = await supabase
    .from('agent_profiles')
    .insert({ user_id: userId })
    .select()
    .single();

  if (createError) throw createError;
  return newProfile as AgentProfile;
}

// Update agent profile
export async function updateAgentProfile(
  userId: string,
  updates: Partial<AgentProfile>
): Promise<void> {
  const { error } = await supabase
    .from('agent_profiles')
    .update(updates)
    .eq('user_id', userId);

  if (error) throw error;
}

// Calculate relevance score for a product given agent profile
function calculateRelevanceScore(
  product: any,
  agentProfile: AgentProfile | null
): number {
  if (!agentProfile) return 50; // Default score for users without profile

  let score = 50; // Base score

  // HIGHEST PRIORITY: Boost based on structured goals
  if (agentProfile.goals && Array.isArray(agentProfile.goals) && agentProfile.goals.length > 0) {
    const goals = agentProfile.goals[0];
    
    // Check if goals is structured object
    if (goals && typeof goals === 'object' && 'volume_target' in goals) {
      const structuredGoals = goals as any;
      const categories = Array.isArray(product.categories) ? product.categories : [];
      const productTitle = product.title?.toLowerCase() || '';

      // Volume target influences tool complexity
      if (structuredGoals.volume_target) {
        if (structuredGoals.volume_target <= 10) {
          // Low volume: simple, affordable tools
          if (categories.includes('productivity')) score += 20;
          if (categories.includes('coaching')) score += 15;
          if (product.price && product.price < 100) score += 10;
        } else if (structuredGoals.volume_target <= 30) {
          // Mid volume: automation + CRM
          if (categories.includes('crm')) score += 25;
          if (categories.includes('marketing-automation')) score += 20;
          if (categories.includes('lead-generation')) score += 20;
        } else {
          // High volume: team tools, advanced systems
          if (categories.includes('data-analytics')) score += 25;
          if (categories.includes('transaction-management')) score += 20;
          if (productTitle.includes('team') || productTitle.includes('enterprise')) score += 20;
        }
      }

      // Business mix influences product type
      if (structuredGoals.business_mix === 'buyers' && productTitle.includes('buyer')) score += 15;
      if (structuredGoals.business_mix === 'sellers' && (productTitle.includes('listing') || productTitle.includes('seller'))) score += 15;
      if (structuredGoals.business_mix === 'investors' && productTitle.includes('investment')) score += 20;

      // Niche alignment
      if (structuredGoals.niche && Array.isArray(structuredGoals.niche)) {
        structuredGoals.niche.forEach((n: string) => {
          if (n === 'luxury' && productTitle.includes('luxury')) score += 15;
          if (n === 'new_construction' && productTitle.includes('construction')) score += 15;
          if (n === 'investment' && productTitle.includes('investment')) score += 15;
        });
      }

      // Lead strategy influences recommendations
      if (structuredGoals.lead_strategy === 'leads' && categories.includes('lead-generation')) score += 25;
      if (structuredGoals.lead_strategy === 'referral' && (categories.includes('crm') || productTitle.includes('referral'))) score += 20;
      if (structuredGoals.lead_strategy === 'sphere' && (categories.includes('marketing-automation') || productTitle.includes('email'))) score += 20;
      if (structuredGoals.lead_strategy === 'team' && productTitle.includes('team')) score += 15;

      // Work style influences tool complexity
      if (structuredGoals.work_style === 'go_getter' && categories.includes('lead-generation')) score += 15;
      if (structuredGoals.work_style === 'relationship' && (categories.includes('crm') || productTitle.includes('relationship'))) score += 15;
      if (structuredGoals.work_style === 'analytical' && (categories.includes('data-analytics') || productTitle.includes('analytics'))) score += 15;
      if (structuredGoals.work_style === 'team_player' && productTitle.includes('team')) score += 15;

      // Growth path influences tool selection
      if (structuredGoals.growth_path === 'build_team' && productTitle.includes('team')) score += 20;
      if (structuredGoals.growth_path === 'scale_existing' && (categories.includes('productivity') || categories.includes('transaction-management'))) score += 20;
      if (structuredGoals.growth_path === 'stay_solo' && product.price && product.price < 200) score += 10;
    }
  }

  // Career stage alignment (lower priority now)
  if (product.best_for && agentProfile.career_stage) {
    const bestFor = Array.isArray(product.best_for) ? product.best_for : [];
    const stageKeywords = {
      new_agent: ['new', 'beginner', 'starter'],
      growing: ['growing', 'intermediate'],
      established: ['established', 'professional'],
      team_leader: ['team', 'leader', 'manager'],
      top_producer: ['top', 'producer', 'advanced'],
    };

    const keywords = stageKeywords[agentProfile.career_stage as keyof typeof stageKeywords] || [];
    const hasMatch = bestFor.some((item: any) => {
      const text = typeof item === 'string' ? item : item.text || '';
      return keywords.some(kw => text.toLowerCase().includes(kw));
    });

    if (hasMatch) score += 15;
  }

  // Boost based on budget alignment
  if (product.price && agentProfile.budget_range) {
    const budgetLimits = {
      low: 200,
      mid: 500,
      high: 1000,
    };

    const limit = budgetLimits[agentProfile.budget_range as keyof typeof budgetLimits];
    if (product.price <= limit) {
      score += 15;
    } else {
      score -= 10;
    }
  }

  // Boost for popular/rated products
  if (product.rating && product.rating >= 4.5) {
    score += 10;
  }

  return Math.min(100, Math.max(0, score));
}

// Create summary of product capabilities for AI context
function computeCapabilitiesSummary(product: any): string {
  if (!product.product_capabilities || product.product_capabilities.length === 0) {
    return 'No capabilities data';
  }

  const capabilities = product.product_capabilities
    .filter((c: any) => c.has_capability)
    .map((c: any) => c.capability_key)
    .join(', ');

  return capabilities || 'Basic features';
}

// Extract integration tags for AI context
function getIntegrationTags(product: any): string[] {
  if (!product.product_integrations || product.product_integrations.length === 0) {
    return [];
  }

  return product.product_integrations
    .map((i: any) => i.integrates_with_name)
    .filter(Boolean);
}

// Get enriched products for agent context
export async function getEnrichedProductsForAgent(
  agentProfile: AgentProfile | null
): Promise<EnrichedProduct[]> {
  try {
    const { data: products, error } = await supabase
      .from('products')
      .select(`
        id, slug, title, subtitle, image, price, msrp, price_duration, rating, categories,
        best_for, at_a_glance, service_type,
        product_capabilities(category, capability_key, has_capability),
        product_integrations!product_integrations_product_id_fkey(integrates_with_name)
      `)
      .eq('is_published', true)
      .order('rating', { ascending: false })
      .limit(50);

    if (error) throw error;
    if (!products) return [];

    // Enrich with scores and summaries
    const enriched = products.map((p: any) => ({
      id: p.id,
      slug: p.slug,
      title: p.title,
      subtitle: p.subtitle,
      image: p.image,
      categories: p.categories,
      price: p.price,
      msrp: p.msrp,
      price_duration: p.price_duration,
      rating: p.rating,
      service_type: p.service_type,
      best_for: p.best_for,
      at_a_glance: p.at_a_glance,
      relevance_score: calculateRelevanceScore(p, agentProfile),
      capabilities_summary: computeCapabilitiesSummary(p),
      integration_tags: getIntegrationTags(p),
      starting_price: p.price || p.msrp || 0
    }));

    // Sort by relevance score and return top 20
    return enriched
      .sort((a, b) => b.relevance_score - a.relevance_score)
      .slice(0, 20);
  } catch (error) {
    console.error('Error fetching enriched products:', error);
    return [];
  }
}

/**
 * Get user's tech stack for AI context
 * Returns simplified list of tools they already own
 */
export async function getTechStackForAI(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('user_tech_stack')
    .select(`
      product:products(title)
    `)
    .eq('user_id', userId)
    .eq('status', 'owned');

  if (error) return [];
  if (!data) return [];

  // Extract product titles
  return data
    .map((item: any) => item.product?.title)
    .filter(Boolean);
}

// Get all conversations for user
export async function getAllConversationsForUser(userId: string) {
  const { data, error } = await supabase
    .from('chat_conversations')
    .select('id, conversation_summary, last_message_at, started_at')
    .eq('user_id', userId)
    .order('last_message_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Create new conversation explicitly
export async function createNewConversation(
  userId: string,
  agentProfileId?: string,
  initialTitle?: string
): Promise<string> {
  const { data, error } = await supabase
    .from('chat_conversations')
    .insert({
      user_id: userId,
      agent_profile_id: agentProfileId,
      conversation_summary: initialTitle || null
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

// Get or create conversation for user (for backward compatibility)
export async function getOrCreateConversation(
  userId: string,
  agentProfileId?: string
): Promise<string> {
  // Check for existing active conversation
  const { data: existing } = await supabase
    .from('chat_conversations')
    .select('id')
    .eq('user_id', userId)
    .order('last_message_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing) return existing.id;

  // Create new conversation
  return createNewConversation(userId, agentProfileId);
}

// Update conversation title/summary
export async function updateConversationTitle(
  conversationId: string,
  title: string
): Promise<void> {
  const { error } = await supabase
    .from('chat_conversations')
    .update({ conversation_summary: title })
    .eq('id', conversationId);

  if (error) throw error;
}

// Delete conversation
export async function deleteConversation(conversationId: string): Promise<void> {
  // Delete messages first
  await supabase
    .from('chat_messages')
    .delete()
    .eq('conversation_id', conversationId);

  // Delete conversation
  const { error } = await supabase
    .from('chat_conversations')
    .delete()
    .eq('id', conversationId);

  if (error) throw error;
}

// Get conversation messages
export async function getConversationMessages(conversationId: string, limit = 10) {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).reverse(); // Return in chronological order
}

// Save message
export async function saveMessage(
  conversationId: string,
  role: 'user' | 'assistant' | 'system',
  content: string,
  metadata?: any
) {
  const { error } = await supabase
    .from('chat_messages')
    .insert({
      conversation_id: conversationId,
      role,
      content,
      metadata
    });

  if (error) throw error;

  // Update conversation last_message_at
  await supabase
    .from('chat_conversations')
    .update({ last_message_at: new Date().toISOString() })
    .eq('id', conversationId);
}
